import React, { useState, useEffect } from 'react';
import { User as UserIcon, Settings, Bell, Shield, UserCog } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { updateUser, getUserById } from '../api/users';
import { getNotificationPreferences, updateNotificationPreferences } from '../api/notifications';
import Card from '../components/Card';
import UserForm from '../components/UserForm';
import ChangePasswordForm from '../components/ChangePasswordForm';
import ThemeSelector from '../components/ThemeSelector';
import { NotificationPreferencesForm } from '../components/notifications/NotificationPreferencesForm';
import UserRoles from '../components/UserRoles';
import TabNavigation from '../components/TabNavigation';
import Breadcrumb from '../components/Breadcrumb';
import type { User } from '../types/user';
import type { NotificationPreference, NotificationPreferenceUpdate } from '../types/notification';
import toast from 'react-hot-toast';

const ProfilePage: React.FC = () => {
  const { user: authUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreference | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<string>('personal');

  useEffect(() => {
    const loadUserData = async () => {
      if (!authUser?.id) return;

      try {
        setLoading(true);
        const [userData, notifData] = await Promise.allSettled([
          getUserById(authUser.id),
          getNotificationPreferences(),
        ]);

        if (userData.status === 'fulfilled') {
          setUser(userData.value);
        }

        if (notifData.status === 'fulfilled') {
          setNotificationPrefs(notifData.value);
        }
      } catch (err) {
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [authUser]);

  const handleUpdateUser = async (updatedUser: User) => {
    try {
      await updateUser(updatedUser.id, updatedUser);
      setUser(updatedUser);
      toast.success('Profile updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      toast.error(errorMessage);
      throw err;
    }
  };

  const handleUpdateNotifications = async (data: NotificationPreferenceUpdate) => {
    try {
      const updated = await updateNotificationPreferences(data);
      setNotificationPrefs(updated);
      toast.success('Notification preferences updated');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update preferences';
      toast.error(errorMessage);
      throw err;
    }
  };

  const sections = [
    { id: 'personal', label: 'Personal Info', icon: UserIcon },
    { id: 'account', label: 'Account', icon: Settings },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'roles', label: 'Roles & Permissions', icon: Shield },
  ];

  const tabs = sections.map(({ id, label }) => ({ id, label }));

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-8">
        <div className="loading-container h-64">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Dashboard', path: '/' },
          { label: 'Profile' }
        ]}
      />

      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <div className="p-3 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl">
            <UserCog className="text-white" size={28} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">
              My Profile
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your account settings and preferences
            </p>
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeSection}
        onTabChange={setActiveSection}
        className="mb-6"
      />

      {/* Section Content */}
      <div className="space-y-6">
        {activeSection === 'personal' && user && (
          <Card variant="elevated">
            <Card.Header 
              title="Personal Information" 
              subtitle="Update your profile details and bio"
            />
            <Card.Body>
              <UserForm user={user} onSuccess={handleUpdateUser} />
            </Card.Body>
          </Card>
        )}

        {activeSection === 'account' && (
          <>
            <Card variant="elevated">
              <Card.Header 
                title="Theme Preference" 
                subtitle="Customize the interface appearance"
              />
              <Card.Body>
                <ThemeSelector />
              </Card.Body>
            </Card>

            <Card variant="elevated">
              <Card.Header 
                title="Password Security" 
                subtitle="Change your password to keep your account secure"
              />
              <Card.Body>
                <ChangePasswordForm />
              </Card.Body>
            </Card>
          </>
        )}

        {activeSection === 'notifications' && (
          <Card variant="elevated">
            <Card.Header 
              title="Notification Settings" 
              subtitle="Configure how and when you receive notifications"
            />
            <Card.Body>
              <NotificationPreferencesForm
                preferences={notificationPrefs}
                onSubmit={handleUpdateNotifications}
                loading={loading}
              />
            </Card.Body>
          </Card>
        )}

        {activeSection === 'roles' && user && (
          <Card variant="elevated">
            <Card.Header 
              title="Roles & Permissions" 
              subtitle="View your access levels and permissions across projects and programmes"
            />
            <Card.Body>
              <div className="space-y-4">
                {/* System Role */}
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="text-primary-600 dark:text-primary-400" size={18} />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      System Role
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`
                      px-3 py-1 rounded-full text-xs font-medium
                      ${user.system_role === 'Admin'
                        ? 'bg-accent-100 dark:bg-accent-900/30 text-accent-800 dark:text-accent-300'
                        : user.system_role === 'Member'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }
                    `}>
                      {user.system_role || 'User'}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Global access level
                    </span>
                  </div>
                </div>

                {/* Project & Programme Roles */}
                <UserRoles userId={user.id} />
              </div>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
